import React, { useMemo } from "react";
import BookOutlinedIcon from "@mui/icons-material/BookOutlined";
import CycloneIcon from "@mui/icons-material/Cyclone";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import LetterWheel, { buildWheelTiles, lettersForWheel } from "./LetterWheel";
import PlayBackground from "./PlayBackground";
import ClueBanner from "./ClueBanner";
import {
    buildClueMapFromDisplayClue,
    buildDisplayGrid,
    buildWordClueNumbers,
    formatCellWordNumberLabel,
    normalizeWord,
    parseWordPositions,
    puzzleCellKeys,
    resolveWordWheelGridSize,
    usedPuzzleBounds,
} from "../lib/gridReveal";
import { GRID_CREAM } from "../lib/gridTheme";
import { resolveScreenshotPlayColors } from "../lib/theme";
import { WW_SHOT_PLAY_TOP_INSET } from "../styles/screenshotStyles";

const CLUE_PLACEHOLDER = "Tap a numbered cell to see the clue";
const DEMO_COINS = 45;

function parseWords(wordsInUse) {
    if (!wordsInUse) return [];
    return wordsInUse.split("\n").map((w) => w.trim()).filter(Boolean);
}

/** Tile indices that spell `word` on the wheel (each tile used at most once). */
function findWheelIndicesForWord(tiles, word) {
    const letters = normalizeWord(word).split("");
    if (letters.length === 0) return null;
    const used = new Set();
    const indices = [];
    for (const ch of letters) {
        let found = -1;
        for (let i = 0; i < tiles.length; i += 1) {
            if (used.has(i)) continue;
            if ((tiles[i].letter || "").toUpperCase() === ch) {
                found = i;
                break;
            }
        }
        if (found < 0) return null;
        used.add(found);
        indices.push(found);
    }
    return indices;
}

/**
 * Prefer an unfound 3?? letter word that can be spelled on the wheel (4 before 3).
 * When `preferredWord` is set, use it if the wheel can spell it.
 * @returns {{ word: string, indices: number[] } | null}
 */
function pickDemoDragSelection(targetWords, foundSet, tiles, preferredWord = null) {
    if (preferredWord) {
        const word = normalizeWord(preferredWord);
        const indices = findWheelIndicesForWord(tiles, word);
        if (indices) {
            return { word, indices };
        }
    }
    const unfound = targetWords.filter((w) => w && !foundSet.has(w));
    const byLen = (len) => unfound.filter((w) => w.length === len);
    for (const word of [...byLen(4), ...byLen(3), ...byLen(5)]) {
        const indices = findWheelIndicesForWord(tiles, word);
        if (indices) {
            return { word, indices };
        }
    }
    return null;
}

function TwoCoinsIcon({ size = 18, color = "#facc15" }) {
    return (
        <Box
            component="svg"
            width={size}
            height={size}
            viewBox="0 0 512 512"
            aria-hidden
            sx={{ display: "block", flexShrink: 0 }}
        >
            <path
                fill={color}
                d="M264.4 95.01c-35.6-.06-80.2 11.19-124.2 34.09C96.27 152 61.45 182 41.01 211.3c-20.45 29.2-25.98 56.4-15.92 75.8 10.07 19.3 35.53 30.4 71.22 30.4 35.69.1 80.29-11.2 124.19-34 44-22.9 78.8-53 99.2-82.2 20.5-29.2 25.9-56.4 15.9-75.8-10.1-19.3-35.5-30.49-71.2-30.49zm91.9 70.29c-3.5 15.3-11.1 31-21.8 46.3-22.6 32.3-59.5 63.8-105.7 87.8-46.2 24.1-93.1 36.2-132.5 36.2-18.6 0-35.84-2.8-50.37-8.7l10.59 20.4c10.08 19.4 35.47 30.5 71.18 30.5 35.7 0 80.3-11.2 124.2-34.1 44-22.8 78.8-52.9 99.2-82.2 20.4-29.2 26-56.4 15.9-75.7zm28.8 16.8c11.2 26.7 2.2 59.2-19.2 89.7-18.9 27.1-47.8 53.4-83.6 75.4 11.1 1.2 22.7 1.8 34.5 1.8 49.5 0 94.3-10.6 125.9-27.1 31.7-16.5 49.1-38.1 49.1-59.9 0-21.8-17.4-43.4-49.1-59.9-16.1-8.4-35.7-15.3-57.6-20zm106.7 124.8c-10.2 11.9-24.2 22.4-40.7 31-35 18.2-82.2 29.1-134.3 29.1-21.2 0-41.6-1.8-60.7-5.2-23.2 11.7-46.5 20.4-68.9 26.1 1.2.7 2.4 1.3 3.7 2 31.6 16.5 76.4 27.1 125.9 27.1s94.3-10.6 125.9-27.1c31.7-16.5 49.1-38.1 49.1-59.9z"
            />
        </Box>
    );
}

function TreasureChestIcon({ size = 18, color = "#facc15" }) {
    return (
        <Box
            component="svg"
            width={size}
            height={size}
            viewBox="0 0 256 256"
            aria-hidden
            sx={{ display: "block", flexShrink: 0 }}
        >
            <path
                fill={color}
                d="M184,40H72A56.06,56.06,0,0,0,16,96v96a16,16,0,0,0,16,16H224a16,16,0,0,0,16-16V96A56.06,56.06,0,0,0,184,40Zm40,56v8H192V56.8A40.07,40.07,0,0,1,224,96Zm-88,40H120V104h16Zm-24,16h32a8,8,0,0,0,8-8V120h24v72H80V120h24v24A8,8,0,0,0,112,152Zm40-48V96a8,8,0,0,0-8-8H112a8,8,0,0,0-8,8v8H80V56h96v48ZM64,56.8V104H32V96A40.07,40.07,0,0,1,64,56.8ZM32,120H64v72H32Zm192,72H192V120h32v72Z"
            />
        </Box>
    );
}

/**
 * Static play panel for App Store screenshots ??matches word-wheel-quest PlayScreen themes.
 * Pass `fill` for tall 9:16 capture frames so grid + wheel stretch the full height.
 */
export default function ScreenshotPlayPanel({
    puzzle,
    foundWords = [],
    journeyLevel = null,
    background = "deep_sea",
    theme = "random",
    fill = false,
    topInset = false,
    /** Slightly smaller grid/wheel for the homepage phone mockup. */
    compact = false,
    /** Feature-graphic phones: keep grid, clue, and wheel as a group in the middle. */
    centerContent = false,
    /** Prefer this word for the static drag line on the wheel (e.g. "GUT"). */
    demoDragWord = null,
    /** Screenshot 02: show the clue meaning, keep the word unsolved (no wheel spoiler). */
    clueMode = false,
    showBubbles = true,
    /** When set, show this word's clue (landing playback). */
    clueWord = null,
    /** When set (including `[]`), use these wheel indices instead of the static demo drag. */
    dragIndices = undefined,
    /** PC 1920×1080: grid on the left, clue and wheel on the right. */
    splitColumns = false,
    /** Optional block above the clue banner (PC dictionary card). */
    aboveClue = null,
    /** Landscape ad phones: show the app "Level N" title. */
    showLevelTitle = false,
}) {
    const playColors = useMemo(() => resolveScreenshotPlayColors(theme), [theme]);
    const targetWords = useMemo(() => parseWords(puzzle?.wordsInUse).map(normalizeWord), [puzzle]);
    const foundSet = useMemo(() => new Set(foundWords.map(normalizeWord)), [foundWords]);
    const baseWheelLetters = useMemo(
        () => lettersForWheel(targetWords, puzzle?.lettersGrid, puzzle?.details),
        [targetWords, puzzle]
    );
    const wheelTiles = useMemo(
        () => buildWheelTiles(baseWheelLetters, puzzle?.id || "screenshot-wheel"),
        [baseWheelLetters, puzzle?.id]
    );
    const wordPositions = useMemo(
        () => parseWordPositions(puzzle?.filledCoordinates),
        [puzzle?.filledCoordinates]
    );
    const puzzleCells = useMemo(() => puzzleCellKeys(wordPositions), [wordPositions]);
    const gridSize = useMemo(() => resolveWordWheelGridSize(puzzle), [puzzle]);
    const displayGrid = useMemo(
        () => buildDisplayGrid(foundWords, wordPositions, new Map(), gridSize),
        [foundWords, wordPositions, gridSize]
    );
    const { wordToNumber, cellToNumbers } = useMemo(
        () => buildWordClueNumbers(puzzle?.filledCoordinates),
        [puzzle?.filledCoordinates]
    );
    const gridBounds = useMemo(
        () => usedPuzzleBounds(puzzleCells, gridSize),
        [puzzleCells, gridSize]
    );
    const visibleRows = Math.max(1, gridBounds.maxRow - gridBounds.minRow + 1);
    const visibleCols = Math.max(1, gridBounds.maxCol - gridBounds.minCol + 1);
    const clueMap = useMemo(
        () => buildClueMapFromDisplayClue(puzzle?.displayClue),
        [puzzle?.displayClue]
    );

    const dragSelection = useMemo(() => {
        if (dragIndices !== undefined) {
            return { word: normalizeWord(clueWord || demoDragWord || ""), indices: dragIndices };
        }
        if (clueMode) return null;
        return pickDemoDragSelection(targetWords, foundSet, wheelTiles, demoDragWord);
    }, [targetWords, foundSet, wheelTiles, demoDragWord, clueMode, clueWord, dragIndices]);
    const selectedIndices = dragSelection?.indices ?? [];
    const dragWord = dragSelection?.word ?? null;

    // Clue + grid highlight follow the word being dragged on the wheel.
    // Clue mode: pick an unfound word that has a definition so the meaning is visible.
    const previewWord = clueWord
        ? normalizeWord(clueWord)
        : dragIndices !== undefined
            ? null
            : (clueMode
                ? targetWords.find((w) => !foundSet.has(w) && clueMap.get(w))
                : null)
                || dragWord
                || targetWords.find((w) => !foundSet.has(w))
                || foundWords[0]
                || targetWords[0];
    const previewWordNumber = previewWord ? wordToNumber.get(previewWord) ?? null : null;
    const previewClue = previewWord ? (clueMap.get(previewWord) || "") : "";
    const selectedWordCells = useMemo(() => {
        if (!previewWord || !wordPositions[previewWord]) return new Set();
        return new Set(wordPositions[previewWord].map((p) => `${p.row},${p.col}`));
    }, [previewWord, wordPositions]);

    const wheelColors = useMemo(
        () => ({
            ...playColors,
            surface: playColors.letterBg,
            border: playColors.letterBorder,
            textOnSurface: playColors.letterText,
        }),
        [playColors]
    );

    const shotTools = fill && !compact;
    const toolBtnSize = showLevelTitle
        ? "clamp(22px, 8cqmin, 36px)"
        : shotTools
        ? "clamp(40px, 13cqmin, 68px)"
        : compact
            ? "clamp(24px, 10cqmin, 34px)"
            : "clamp(32px, 14cqmin, 48px)";
    const toolIconSize = showLevelTitle
        ? "clamp(14px, 4.6cqmin, 20px)"
        : shotTools
        ? "clamp(22px, 7.5cqmin, 36px)"
        : compact
            ? "clamp(16px, 8cqmin, 22px)"
            : "clamp(18px, 9cqmin, 24px)";
    const toolStackWidth = showLevelTitle
        ? "clamp(26px, 9cqmin, 40px)"
        : shotTools
        ? "clamp(44px, 14cqmin, 72px)"
        : compact
            ? "clamp(28px, 11cqmin, 38px)"
            : "clamp(36px, 15cqmin, 52px)";
    const toolbarButtonSx = {
        width: toolBtnSize,
        height: toolBtnSize,
        minWidth: toolBtnSize,
        minHeight: toolBtnSize,
        p: 0.4,
        bgcolor: playColors.toolBtnBg,
        border: `1px solid ${playColors.borderStrong}`,
        boxShadow: playColors.shadowSm,
        color: playColors.toolIcon,
        pointerEvents: "none",
    };

    const coinColor = theme === "random" ? "#ca8a04" : "#facc15";

    const clueText = previewWord
        ? `${previewWordNumber != null ? `${previewWordNumber}. ` : ""}${previewClue || "No clue available for this word."}`
        : CLUE_PLACEHOLDER;

    return (
        <Box
            sx={{
                position: "relative",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                borderRadius: fill ? 0 : 3,
                // Size relative to the phone / capture frame ??not the browser viewport.
                containerType: "size",
                containerName: "ww-play-panel",
                ...(fill
                    ? { height: "100%", minHeight: 0, flex: 1, width: "100%" }
                    : { width: "100%", height: "100%" }),
            }}
        >
            <PlayBackground background={background} theme={theme} showBubbles={showBubbles} />
            <Box
                sx={{
                    position: "relative",
                    zIndex: 1,
                    display: "flex",
                    flexDirection: "column",
                    px: fill ? (compact ? 0.75 : 2) : 0.5,
                    pt: fill && topInset
                        ? (typeof topInset === "number"
                            ? `${topInset}px`
                            : WW_SHOT_PLAY_TOP_INSET)
                        : fill
                          ? 0.5
                          : 0.5,
                    pb: fill ? (showLevelTitle ? 0.5 : shotTools ? 1.75 : compact ? 0.5 : 0.75) : 0.75,
                    boxSizing: "border-box",
                    ...(splitColumns
                        ? {
                              flex: 1,
                              minHeight: 0,
                              height: "100%",
                              width: "100%",
                              flexDirection: "row",
                              alignItems: "stretch",
                              justifyContent: "center",
                              gap: "28px",
                              px: 3,
                              pt: 1,
                              pb: 2,
                          }
                        : fill
                        ? {
                              flex: 1,
                              minHeight: 0,
                              height: "100%",
                              width: "100%",
                              justifyContent: centerContent ? "center" : "flex-start",
                              alignItems: "stretch",
                              gap: compact ? "6px" : clueMode && !compact ? "12px" : "10px",
                              ...(centerContent ? { pt: "12%", pb: "8%" } : {}),
                          }
                        : {
                              height: "100%",
                              minHeight: 0,
                              justifyContent: "space-between",
                              gap: 0.75,
                          }),
                }}
            >
                {(showLevelTitle || (centerContent && journeyLevel != null)) ? (
                    <Typography
                        sx={{
                            flexShrink: 0,
                            width: "100%",
                            textAlign: "center",
                            fontWeight: 800,
                            fontSize: centerContent
                                ? "clamp(13px, 7cqmin, 18px)"
                                : "clamp(11px, 5.2cqmin, 18px)",
                            lineHeight: 1.1,
                            letterSpacing: "-0.2px",
                            color: "#ffffff",
                            textShadow: "0 1px 4px rgba(0,0,0,0.8)",
                        }}
                    >
                        {`Level ${journeyLevel ?? 1}`}
                    </Typography>
                ) : null}
                <Box
                    sx={{
                        width: splitColumns ? "auto" : "100%",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        ...(splitColumns
                            ? {
                                  flex: "1 1 50%",
                                  minWidth: 0,
                                  minHeight: 0,
                                  height: "100%",
                                  alignSelf: "stretch",
                              }
                            : fill && !compact
                            ? {
                                  // Size against the play panel container (outer), not this box —
                                  // nested container-type + cqh collapses the board to 0.
                                  flex: "0 0 auto",
                                  maxHeight: showLevelTitle ? "none" : clueMode ? "34cqh" : "38cqh",
                                  overflow: "visible",
                              }
                            : fill
                              ? { flex: "0 0 auto", overflow: "visible" }
                              : { flex: "0 1 auto" }),
                    }}
                >
                    <Box
                        sx={{
                            // Cap board size so promo / clue / wheel keep room on tall store frames.
                            width: splitColumns
                                ? `min(46cqw, calc(88cqh * ${visibleCols} / ${visibleRows}))`
                                : showLevelTitle
                                ? `min(90cqw, calc(34cqh * ${visibleCols} / ${visibleRows}))`
                                : fill && !compact
                                ? `min(92cqw, calc(${clueMode ? 34 : 38}cqh * ${visibleCols} / ${visibleRows}))`
                                : fill && centerContent
                                ? `min(78cqw, calc(26cqh * ${visibleCols} / ${visibleRows}))`
                                : fill
                                    ? `min(84cqw, calc(36cqh * ${visibleCols} / ${visibleRows}))`
                                    : "100%",
                            maxWidth: "100%",
                            maxHeight: splitColumns
                                ? "88cqh"
                                : showLevelTitle
                                ? "34cqh"
                                : fill && !compact
                                ? clueMode
                                    ? "34cqh"
                                    : "38cqh"
                                : fill && centerContent
                                ? "26cqh"
                                : fill
                                    ? "36cqh"
                                    : "min(100%, 52cqh)",
                            minWidth: showLevelTitle ? 0 : fill && !compact ? "min(72cqw, 220px)" : 0,
                            height: showLevelTitle
                                ? `min(calc(90cqw * ${visibleRows} / ${visibleCols}), 34cqh)`
                                : "auto",
                            aspectRatio: `${visibleCols} / ${visibleRows}`,
                            display: "grid",
                            gridTemplateColumns: `repeat(${visibleCols}, minmax(0, 1fr))`,
                            gridTemplateRows: `repeat(${visibleRows}, minmax(0, 1fr))`,
                            gap: fill ? "clamp(2px, 1.1cqmin, 4px)" : "clamp(2px, 1.2cqmin, 5px)",
                            alignItems: "stretch",
                            justifyItems: "stretch",
                        }}
                    >
                        {Array.from({ length: visibleRows }, (_, rowOffset) =>
                            Array.from({ length: visibleCols }, (_, colOffset) => {
                                const row = gridBounds.minRow + rowOffset;
                                const col = gridBounds.minCol + colOffset;
                                const letter = displayGrid[row]?.[col] || "";
                                const cellKey = `${row},${col}`;
                                const isPuzzleCell = puzzleCells.has(cellKey);
                                const isRevealed = Boolean(letter);
                                const isSelected = selectedWordCells.has(cellKey);
                                const wordNumberLabel = formatCellWordNumberLabel(
                                    cellToNumbers.get(cellKey)
                                );
                                const fillColor = !isPuzzleCell
                                    ? "transparent"
                                    : isSelected
                                        ? GRID_CREAM.selectedBg
                                        : isRevealed
                                            ? GRID_CREAM.revealedBg
                                            : GRID_CREAM.cellBg;
                                const borderColor = !isPuzzleCell
                                    ? "transparent"
                                    : isSelected
                                        ? GRID_CREAM.selectedBorder
                                        : GRID_CREAM.cellBorder;
                                const borderWidth = isPuzzleCell
                                    ? (isSelected
                                        ? GRID_CREAM.selectedBorderWidth
                                        : GRID_CREAM.cellBorderWidth)
                                    : 0;
                                const letterColor = isSelected
                                    ? GRID_CREAM.selectedText
                                    : GRID_CREAM.revealedText;
                                return (
                                    <Box
                                        key={`${row}-${col}`}
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            width: "100%",
                                            height: "100%",
                                            boxSizing: "border-box",
                                            position: "relative",
                                            containerType: "size",
                                            fontFamily:
                                                '"Segoe UI", system-ui, -apple-system, sans-serif',
                                            borderRadius: compact ? "4px" : "6px",
                                            bgcolor: fillColor,
                                            color: isRevealed ? letterColor : "transparent",
                                            border: isPuzzleCell
                                                ? `${borderWidth}px solid ${borderColor}`
                                                : "none",
                                            boxShadow: "none",
                                            minWidth: 0,
                                            lineHeight: 1,
                                        }}
                                    >
                                        {wordNumberLabel && isPuzzleCell ? (
                                            <Box
                                                component="span"
                                                sx={{
                                                    position: "absolute",
                                                    top: "4%",
                                                    left: "4%",
                                                    minWidth: compact ? "16cqmin" : "22cqmin",
                                                    minHeight: compact ? "16cqmin" : "22cqmin",
                                                    px: wordNumberLabel.length > 2 ? "6%" : "4%",
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    lineHeight: 1,
                                                    fontSize: compact
                                                        ? "clamp(6px, 18cqmin, 12px)"
                                                        : "clamp(8px, 22cqmin, 22px)",
                                                    fontWeight: 800,
                                                    color: GRID_CREAM.badgeText,
                                                    bgcolor: GRID_CREAM.badgeBg,
                                                    border: `1px solid ${GRID_CREAM.badgeBorder}`,
                                                    borderRadius: "5px",
                                                    zIndex: 2,
                                                    userSelect: "none",
                                                    pointerEvents: "none",
                                                }}
                                            >
                                                {wordNumberLabel}
                                            </Box>
                                        ) : null}
                                        {letter ? (
                                            <Box
                                                component="span"
                                                sx={{
                                                    fontSize: compact
                                                        ? "clamp(12px, 48cqmin, 28px)"
                                                        : "clamp(14px, 42cqmin, 88px)",
                                                    fontWeight: 900,
                                                    lineHeight: 1,
                                                    color: "inherit",
                                                }}
                                            >
                                                {letter}
                                            </Box>
                                        ) : null}
                                    </Box>
                                );
                            })
                        )}
                    </Box>
                </Box>

                <Box
                    sx={{
                        mt: showLevelTitle && !splitColumns && !centerContent ? "auto" : 0,
                        flexShrink: splitColumns ? 1 : 0,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: splitColumns ? "center" : "flex-start",
                        gap: splitColumns ? "18px" : fill ? (compact ? "6px" : "12px") : 0.75,
                        width: splitColumns ? "auto" : "100%",
                        minHeight: 0,
                        minWidth: 0,
                        px: fill && !compact && !splitColumns ? 0.5 : 0,
                        ...(splitColumns
                            ? {
                                  flex: "1 1 46%",
                                  height: "100%",
                                  alignSelf: "stretch",
                                  maxWidth: "48%",
                              }
                            : {}),
                    }}
                >
                {aboveClue}
                <ClueBanner
                    clueText={clueText}
                    hasClue={Boolean(previewWord)}
                    emphasized={clueMode && !compact}
                    compact={compact}
                    flush={fill}
                    screenshot={fill && !compact}
                    textColor={clueMode && !compact ? "#1c1917" : playColors.textOnSurface}
                />

                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        maxWidth: "100%",
                        mx: "auto",
                        alignSelf: "stretch",
                        minHeight: 0,
                        overflow: "hidden",
                        gap: shotTools ? 0.75 : 0.25,
                        flex: "0 0 auto",
                        px: fill && !compact ? 1.25 : 0,
                        boxSizing: "border-box",
                        ...(splitColumns
                            ? {
                                  flex: "1 1 auto",
                                  height: "auto",
                                  maxHeight: "78%",
                              }
                            : fill
                            ? {
                                  height: showLevelTitle
                                      ? "min(72cqw, 32cqh)"
                                      : centerContent
                                      ? "min(50cqw, 26cqh)"
                                      : compact
                                      ? "min(64cqw, 40cqh)"
                                      : "min(68cqw, 30cqh)",
                              }
                            : {
                                  flex: "1 1 auto",
                              }),
                    }}
                >
                    <Stack
                        direction="column"
                        spacing={shotTools ? 0.6 : 0.35}
                        sx={{
                            width: toolStackWidth,
                            flexShrink: 0,
                            alignItems: "center",
                            justifyContent: "center",
                            alignSelf: "stretch",
                            maxHeight: "100%",
                            pb: 0,
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                gap: 0.2,
                                pointerEvents: "none",
                            }}
                        >
                            <TwoCoinsIcon size={shotTools ? toolIconSize : 16} color={coinColor} />
                            <Typography
                                component="span"
                                sx={{
                                    fontSize: shotTools
                                        ? "clamp(11px, 3.6cqmin, 16px)"
                                        : "clamp(8px, 5cqmin, 11px)",
                                    fontWeight: 800,
                                    lineHeight: 1.1,
                                    color: coinColor,
                                    textAlign: "center",
                                }}
                            >
                                {DEMO_COINS}
                            </Typography>
                        </Box>
                        <IconButton aria-label="Hint" sx={toolbarButtonSx}>
                            <LightbulbOutlinedIcon sx={{ fontSize: toolIconSize }} />
                        </IconButton>
                        <IconButton aria-label="Bonus words" sx={toolbarButtonSx}>
                            <TreasureChestIcon size={toolIconSize} color={coinColor} />
                        </IconButton>
                    </Stack>

                    <Box
                        sx={{
                            flex: 1,
                            minWidth: 0,
                            minHeight: 0,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            height: "100%",
                            mx: fill && !compact ? 1 : 0,
                            px: fill && !compact ? 0.75 : 0,
                            overflow: "hidden",
                            containerType: "size",
                            boxSizing: "border-box",
                        }}
                    >
                        {wheelTiles.length > 0 ? (
                            <Box
                                sx={{
                                    width: splitColumns
                                        ? "min(92cqw, 92cqh)"
                                        : fill && !compact
                                        ? "min(88cqw, 96cqh)"
                                        : "min(100cqw, 100cqh)",
                                    height: splitColumns
                                        ? "min(92cqw, 92cqh)"
                                        : fill && !compact
                                        ? "min(88cqw, 96cqh)"
                                        : "min(100cqw, 100cqh)",
                                    aspectRatio: "1 / 1",
                                    maxWidth: "100%",
                                    maxHeight: "100%",
                                    flexShrink: 0,
                                }}
                            >
                                <LetterWheel
                                    tiles={wheelTiles}
                                    selectedIndices={selectedIndices}
                                    onSelectionChange={() => {}}
                                    colors={wheelColors}
                                    fill
                                />
                            </Box>
                        ) : null}
                    </Box>

                    <Stack
                        direction="column"
                        spacing={shotTools ? 0.6 : 0.35}
                        sx={{
                            width: toolStackWidth,
                            flexShrink: 0,
                            alignItems: "center",
                            justifyContent: "center",
                            alignSelf: "stretch",
                            maxHeight: "100%",
                            pb: 0,
                        }}
                    >
                        <IconButton aria-label="Dictionary" sx={toolbarButtonSx}>
                            <BookOutlinedIcon sx={{ fontSize: toolIconSize }} />
                        </IconButton>
                        <IconButton aria-label="Shuffle letters" sx={toolbarButtonSx}>
                            <CycloneIcon sx={{ fontSize: toolIconSize }} />
                        </IconButton>
                    </Stack>
                </Box>
                </Box>
            </Box>
        </Box>
    );
}
